import type { ProgressEvent } from "@agentry/zerovia-contracts";
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router";
import { useApi } from "../../app/ApiProvider";
import { useScope } from "../../app/AuthProvider";
import { useAction, useAsync } from "../../app/useAsync";
import { DISPLAY_TIME_ZONE, parseApiDateTime } from "../../ui/date-time";
import {
	Badge,
	Button,
	Callout,
	Card,
	cx,
	LinkButton,
	PageHeader,
	StatusBadge,
	statusLabel,
	toneForStatus,
	waitReasonLabel,
} from "../../ui/primitives";
import {
	EmptyState,
	ErrorState,
	isNotFound,
	LoadingState,
	NotFoundState,
	recoveryFor,
} from "../../ui/states";

export function ProgressListPage() {
	const { client } = useApi();
	const scope = useScope();
	const data = useAsync(
		() => client.listExecutions(scope),
		[client, scope.tenantId, scope.projectId],
	);

	return (
		<>
			<PageHeader
				eyebrow="進捗"
				title="作業の進み具合"
				description="止まっている作業には理由が表示されます。あなたの操作が必要なものは強調されます。"
			/>
			{data.status === "loading" && <LoadingState />}
			{data.status === "error" && <ErrorState error={data.error} onRetry={data.reload} />}
			{data.status === "ready" &&
				(data.data.items.length === 0 ? (
					<EmptyState
						title="進行中の作業はありません"
						description="依頼を始めるとここに表示されます。"
					/>
				) : (
					<ul className="space-y-2">
						{data.data.items.map((execution) => (
							<li key={execution.id}>
								<Link
									to={`/app/progress/${execution.id}`}
									className="ag-card flex flex-wrap items-center justify-between gap-3 px-4 py-3 transition-colors hover:border-brand-300"
								>
									<div className="min-w-0">
										<p className="truncate text-[14.5px] font-semibold text-ink-900">
											{waitReasonLabel(execution.waitReason) ?? statusLabel(execution.status)}
										</p>
										<p className="mt-0.5 truncate font-mono text-[11.5px] text-ink-400">
											{execution.id}
										</p>
									</div>
									<div className="flex shrink-0 items-center gap-2">
										{execution.failureClass && (
											<Badge tone="danger">{execution.failureClass}</Badge>
										)}
										<StatusBadge status={execution.status} />
									</div>
								</Link>
							</li>
						))}
					</ul>
				))}
		</>
	);
}

/**
 * 進捗詳細。
 * SSEでEventを受けるが、sequenceに欠落を検出したら推測で埋めず
 * Timeline Snapshotを取り直す（Handoff条件）。
 */
export function ProgressDetailPage() {
	const { executionId = "" } = useParams();
	const { client } = useApi();
	const scope = useScope();
	const [reloadSeq, setReloadSeq] = useState(0);
	const [events, setEvents] = useState<ProgressEvent[]>([]);
	const [gapRecovered, setGapRecovered] = useState(false);
	const [streamError, setStreamError] = useState<unknown>(null);
	const lastSequence = useRef(-1);

	const data = useAsync(
		() => client.getTimeline(scope, executionId),
		[client, scope.tenantId, scope.projectId, executionId, reloadSeq],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: Timeline確定時とreload時にだけ購読し直す
	useEffect(() => {
		if (data.status !== "ready") return;
		setEvents([]);
		lastSequence.current = -1;
		const subscription = client.subscribeProgress(scope, executionId, {
			onEvent(event) {
				lastSequence.current = event.sequence;
				setEvents((prev) => [...prev, event]);
			},
			onGap() {
				// 欠落を検出したらSnapshotを取り直す。欠けたEventを推測しない。
				setGapRecovered(true);
				setReloadSeq((n) => n + 1);
			},
			onError(error) {
				setStreamError(error);
			},
		});
		return () => subscription.close();
	}, [client, executionId, scope.tenantId, scope.projectId, data.status, reloadSeq]);

	const command = useAction(
		async (kind: "retry" | "cancel" | "resume" | "pause", stateVersion: number) =>
			client.sendWorkflowCommand(scope, executionId, kind, {
				reason: `ユーザー操作: ${kind}`,
				expectedStateVersion: stateVersion,
				idempotencyKey: `${kind}-${executionId}-${stateVersion}`,
			}),
	);

	if (data.status === "loading") return <LoadingState label="進捗を読み込んでいます" />;
	if (data.status === "error") {
		if (isNotFound(data.error)) {
			return <NotFoundState detail="この作業にはアクセスできません。" />;
		}
		return <ErrorState error={data.error} onRetry={data.reload} />;
	}

	const { execution, nodes, dynamicSubtasks } = data.data;
	const terminal = ["SUCCEEDED", "FAILED", "CANCELLED", "TIMED_OUT"].includes(execution.status);
	const doneCount = nodes.filter((n) => n.status === "SUCCEEDED").length;
	const percent = nodes.length === 0 ? 0 : Math.round((doneCount / nodes.length) * 100);

	return (
		<>
			<PageHeader
				eyebrow="進捗"
				title={waitReasonLabel(execution.waitReason) ?? statusLabel(execution.status)}
				description="内部の実行構造ではなく、いまどの工程にいるかを表示しています。"
				actions={
					<LinkButton to={`/app/sessions/${execution.sessionId}`} variant="secondary">
						やりとりへ戻る
					</LinkButton>
				}
			/>

			{execution.status === "WAITING" && execution.waitReason === "USER_APPROVAL" && (
				<Callout tone="warning" title="あなたの承認を待っています">
					<p className="mt-1">内容を確認して、承認するか直してもらうかを選んでください。</p>
					<LinkButton to="/app/approvals" size="sm" className="mt-2">
						承認画面へ
					</LinkButton>
				</Callout>
			)}

			{execution.status === "FAILED" && (
				<Callout tone="danger" title="作業が失敗しました">
					<p className="mt-1">
						原因区分: {execution.failureClass ?? "不明"}。
						一時的な問題であれば、やり直しで解決することがあります。
					</p>
				</Callout>
			)}

			{gapRecovered && (
				<Callout tone="neutral" title="通信が途切れたため最新状態を取り直しました">
					進捗の抜けを推測で埋めず、Snapshotから再構成しています。
				</Callout>
			)}

			{streamError !== null && (
				<Callout tone="warning" title="リアルタイム更新を受け取れません">
					画面の再読み込みで最新状態を取得できます。表示中の内容は最後に取得した時点のものです。
				</Callout>
			)}

			<div className="mt-5 grid gap-4 lg:grid-cols-[1fr_320px]">
				<div className="space-y-4">
					<Card title="工程">
						<div className="mb-3">
							<div className="flex items-center justify-between text-[12px] text-ink-500">
								<span>
									{doneCount} / {nodes.length} 工程完了
								</span>
								<span>{percent}%</span>
							</div>
							<div className="mt-1 h-1.5 overflow-hidden rounded-full bg-ink-100">
								<div
									className="h-full rounded-full bg-brand-600"
									style={{ width: `${percent}%` }}
								/>
							</div>
						</div>

						<ol className="space-y-0">
							{nodes.map((node, index) => (
								<li key={node.id} className="flex gap-3">
									<div className="flex flex-col items-center">
										<span
											className={cx(
												"mt-1 flex size-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold",
												node.status === "SUCCEEDED"
													? "bg-accent-500 text-white"
													: node.status === "FAILED"
														? "bg-danger-500 text-white"
														: node.status === "PENDING"
															? "bg-ink-200 text-ink-500"
															: "bg-brand-600 text-white",
											)}
										>
											{node.status === "SUCCEEDED" ? "✓" : index + 1}
										</span>
										{index < nodes.length - 1 && <span className="my-0.5 w-px flex-1 bg-ink-200" />}
									</div>
									<div className="min-w-0 flex-1 pb-3">
										<p className="flex flex-wrap items-center gap-2 text-[14px] text-ink-900">
											{friendlyNodeName(node.nodeId, node.nodeType)}
											<Badge tone={toneForStatus(node.status)}>{statusLabel(node.status)}</Badge>
										</p>
										{node.waitReason && (
											<p className="text-[12.5px] text-ink-500">
												{waitReasonLabel(node.waitReason)}
											</p>
										)}
									</div>
								</li>
							))}
						</ol>
					</Card>

					{dynamicSubtasks.length > 0 && (
						<Card
							title="Agentが行った作業"
							description="Agentが必要と判断して追加した作業です。決められた工程や品質チェックを飛ばすことはできません。"
						>
							<ul className="space-y-1.5">
								{dynamicSubtasks.map((subtask) => (
									<li
										key={subtask.id}
										className="flex items-start justify-between gap-3 text-[13.5px]"
									>
										<span className="text-ink-700">{subtask.objective}</span>
										<Badge tone={toneForStatus(subtask.status)}>
											{statusLabel(subtask.status)}
										</Badge>
									</li>
								))}
							</ul>
						</Card>
					)}

					<Card title="更新の記録" description="リアルタイムで届いた更新です">
						{events.length === 0 ? (
							<p className="text-[13px] text-ink-500">まだ新しい更新はありません。</p>
						) : (
							<ul className="space-y-1">
								{events.map((event) => (
									<li key={event.eventId} className="flex items-start gap-2 text-[13px]">
										<span className="font-mono text-[11px] text-ink-400">#{event.sequence}</span>
										<span className="text-ink-700">{event.message}</span>
									</li>
								))}
							</ul>
						)}
					</Card>
				</div>

				<div className="space-y-4">
					<Card title="操作">
						<div className="space-y-2">
							{!terminal && (
								<>
									<Button
										variant="secondary"
										size="sm"
										className="w-full"
										disabled={command.pending}
										onClick={() =>
											void command.run("pause", execution.stateVersion).then(data.reload)
										}
									>
										一時停止する
									</Button>
									<Button
										variant="danger"
										size="sm"
										className="w-full"
										disabled={command.pending}
										onClick={() =>
											void command.run("cancel", execution.stateVersion).then(data.reload)
										}
									>
										中止する
									</Button>
								</>
							)}
							{execution.status === "SUSPENDED" && (
								<Button
									size="sm"
									className="w-full"
									disabled={command.pending}
									onClick={() =>
										void command.run("resume", execution.stateVersion).then(data.reload)
									}
								>
									再開する
								</Button>
							)}
							{execution.status === "FAILED" && (
								<Button
									size="sm"
									className="w-full"
									disabled={command.pending}
									onClick={() =>
										void command.run("retry", execution.stateVersion).then(data.reload)
									}
								>
									やり直す
								</Button>
							)}
							{terminal && execution.status !== "FAILED" && (
								<p className="text-[13px] text-ink-500">
									この作業は終了しています。再開はできません。
								</p>
							)}
						</div>

						{command.error != null && (
							<div
								role="alert"
								className="mt-3 rounded-lg border border-danger-500/40 bg-danger-100 px-3 py-2"
							>
								<p className="text-[13px] font-bold text-danger-600">
									{recoveryFor(command.error).title}
								</p>
								<p className="text-[12.5px] text-ink-700">{recoveryFor(command.error).hint}</p>
								<Button size="sm" variant="secondary" className="mt-2" onClick={data.reload}>
									最新の状態を取得
								</Button>
							</div>
						)}
					</Card>

					<Card title="この作業の情報">
						<dl className="space-y-2 text-[13px]">
							<div>
								<dt className="text-ink-500">状態</dt>
								<dd>
									<StatusBadge status={execution.status} />
								</dd>
							</div>
							<div>
								<dt className="text-ink-500">開始</dt>
								<dd className="text-ink-900">{formatDateTime(execution.createdAt)}</dd>
							</div>
							<div>
								<dt className="text-ink-500">最終更新</dt>
								<dd className="text-ink-900">{formatDateTime(execution.updatedAt)}</dd>
							</div>
							<div>
								<dt className="text-ink-500">やり直しの起点</dt>
								<dd className="text-ink-900">
									{execution.currentCheckpointId ? "保存済み（途中から再開できます）" : "未作成"}
								</dd>
							</div>
						</dl>
						<p className="mt-3 text-[12px] text-ink-400">
							詳しい実行内訳は組織の管理者が監査ログから確認できます。
						</p>
					</Card>
				</div>
			</div>
		</>
	);
}

/** End Userへ内部Node IDをそのまま見せない。 */
const NODE_LABEL: Record<string, string> = {
	start: "受付",
	interview: "ヒアリング",
	research: "調査",
	"requirement-gate": "要件の確認",
	structure: "構成づくり",
	copy: "文章作成",
	"medical-ad-compliance-gate": "医療広告表現の確認",
	"menu-accuracy-gate": "メニュー内容の確認",
	"evidence-gate": "根拠の確認",
	design: "デザイン案",
	approval: "あなたの承認",
	artifact: "成果物の確定",
	end: "完了",
};

export function friendlyNodeName(nodeId: string, nodeType: string): string {
	const known = NODE_LABEL[nodeId];
	if (known) return known;
	if (nodeType === "QualityGate") return "品質チェック";
	if (nodeType === "UserApproval") return "あなたの承認";
	if (nodeType === "AgentTask") return "作業";
	return nodeId;
}

export function formatDateTime(value: string | null): string {
	if (!value) return "—";
	const date = parseApiDateTime(value);
	if (Number.isNaN(date.getTime())) return value;
	return new Intl.DateTimeFormat("ja-JP", {
		timeZone: DISPLAY_TIME_ZONE,
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
	}).format(date);
}
